import { Plus, Trash2 } from 'lucide-react'
import SelectBuscable from '@/shared/components/SelectBuscable'
import {
  MAXIMO_DE_SOFTWARE,
  NIVELES_DE_SOFTWARE,
  type SoftwareRequerido,
} from '@/features/rh/lib/catalogosDeRequisicion'

/**
 * Paqueteria o sistemas que pide la vacante, hasta tres (RH-02).
 *
 * Las filas se manejan por indice y no por id porque todavia no lo tienen: el
 * id lo pone la base al guardar, y mientras se editan son solo posiciones.
 */
export const PaqueteriaRequerida = ({
  filas,
  onChange,
  deshabilitado = false,
}: {
  filas: SoftwareRequerido[]
  onChange: (filas: SoftwareRequerido[]) => void
  /**
   * En consulta se pintan los mismos controles, apagados, y no un texto
   * aparte: asi el campo no cambia de forma entre ver y editar, que es como
   * se comporta el resto de la pantalla de detalle.
   */
  deshabilitado?: boolean
}) => {
  const cambiar = (indice: number, campo: keyof SoftwareRequerido, valor: string) =>
    onChange(
      filas.map((fila, i) =>
        i === indice ? { ...fila, [campo]: valor } : fila,
      ) as SoftwareRequerido[],
    )

  const agregar = () =>
    onChange([...filas, { name: '', level: 'basic' }])

  const quitar = (indice: number) =>
    onChange(filas.filter((_, i) => i !== indice))

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Paquetería o sistemas requeridos
      </label>

      {filas.length === 0 && (
        <p className="text-sm text-gray-400 italic mb-3">
          Sin paquetería requerida
        </p>
      )}

      <div className="space-y-3">
        {filas.map((fila, indice) => (
          /* El nombre no se estira a todo el ancho: con `flex-1` solo, el
             nivel se iba hasta la orilla de la tarjeta y la fila se leia
             partida en dos. */
          <div key={indice} className="flex gap-3 items-start">
            <input
              type="text"
              maxLength={80}
              value={fila.name}
              onChange={(e) => cambiar(indice, 'name', e.target.value)}
              disabled={deshabilitado}
              className="flex-1 max-w-max px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600"
              placeholder="Ej: Excel, AutoCAD, SAP"
            />
            <SelectBuscable
              variante="formulario"
              value={fila.level}
              onChange={(valor) => cambiar(indice, 'level', valor)}
              disabled={deshabilitado}
              canDelete={false}
              className="w-44"
              placeholderDeBusqueda="Buscar nivel..."
              opciones={NIVELES_DE_SOFTWARE}
            />
            {!deshabilitado && (
              <button
                type="button"
                onClick={() => quitar(indice)}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                aria-label={`Quitar ${fila.name || 'programa'}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {!deshabilitado && filas.length < MAXIMO_DE_SOFTWARE && (
        <button
          type="button"
          onClick={agregar}
          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#477FFA] hover:text-[#2f63cc] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Agregar programa
        </button>
      )}

      {!deshabilitado && filas.length >= MAXIMO_DE_SOFTWARE && (
        <p className="mt-3 text-xs text-gray-500">
          Máximo {MAXIMO_DE_SOFTWARE} programas.
        </p>
      )}
    </div>
  )
}
